import { FiltersContextProvider } from '@openx/components-filters/lib/context/FiltersContext.tsx';
import type { FiltersContextProviderProps } from '@openx/components-filters/lib/context/types.ts';
import { Table, TableName } from '@openx/components-table/Table.tsx';
import { CriteriaDimension } from '@openx/types/tableCriteria.ts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchRequiredOptions } from '../hooks/useFetchRequiredOptions.ts';
import { CreativesTopBar } from './CreativesTopBar.tsx';
import { useCreativesListFetch } from './hooks/useCreativesListFetch.ts';
import { useRowActions } from './hooks/useRowActions.ts';
import { useTableColumns } from './hooks/useTableColumns.tsx';
import type { CreativesListFilters } from './types.ts';
import { getFiltersConfig } from './utils.ts';

export const CreativesList = () => {
  const { creatives, error, loading, criteria, handleCriteriaChange } = useCreativesListFetch();
  const { advertiserOptions, isLoading: advertisersLoading } = useFetchRequiredOptions();
  const columns = useTableColumns({ advertiserOptions, searchPhrase: criteria[CriteriaDimension.FILTERS]?.phrase });
  const rowActions = useRowActions();
  const { t } = useTranslation();

  const onFiltersChange = useCallback(
    (value: CreativesListFilters) => {
      handleCriteriaChange({
        dimension: CriteriaDimension.FILTERS,
        value,
      });
    },
    [handleCriteriaChange]
  );

  const filtersProviderConfig: FiltersContextProviderProps<CreativesListFilters>['config'] = {
    filtersConfig: getFiltersConfig(t),
    visibleFilters: ['status', 'ad_format'],
    criteria,
    disabledFilters: [],
    onFiltersChange,
  };

  if (error) throw new Error('Creatives Error');

  return (
    <FiltersContextProvider config={filtersProviderConfig}>
      <CreativesTopBar />
      <Table
        name={TableName.CREATIVES}
        data={creatives}
        columns={columns}
        loading={loading || advertisersLoading}
        rowActions={rowActions}
        loadingOnInit
        criteria={criteria}
        onCriteriaChange={handleCriteriaChange}
      />
    </FiltersContextProvider>
  );
};
